import {
	Button,
	CardActions,
	CardContent,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import {
	useCreateAboutUsMutation,
	useUpdateAboutUsMutation,
} from '@/api/information/aboutUsApiSlice';
import {
	InfoRequestDTO,
	InformationAboutUs,
} from '@/api/DTO/information/aboutUs.interface';
import AppRichTextEditor from '@/common/components/editor/AppRichTextEditor';

interface Props {
	type: FormType;
	details?: InformationAboutUs;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function AboutUsInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [createAboutUs] = useCreateAboutUsMutation();
	const [updateAboutUs] = useUpdateAboutUsMutation();

	const defaultValues: DefaultValues<InfoRequestDTO> = {
		titleEn: details?.titleEn || '',
		titleZh: details?.titleZh || '',
		contentEn: details?.contentEn || '',
		contentZh: details?.contentZh || '',
	};

	const formSchema = Yup.object().shape({
		titleZh: Yup.string().required(t('errorMessage.required')),
		contentZh: Yup.string().required(t('errorMessage.required')),
	});
	const methods = useForm<InfoRequestDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<InfoRequestDTO> = async (data) => {
		const obj = { ...data };
		const response =
			type == FormType.create
				? await createAboutUs(obj)
				: await updateAboutUs({ ...obj, id: details?.id || '' });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createPageSuccess')
					: t('snackbar.editPageSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate('/information-manage/about-us-list');
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate('/information-manage/about-us-list');
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('aboutUs.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('aboutUs.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>
						{currentTab == 'zh' && (
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='titleZh'
										label={t('aboutUs.title')}
										maxLength={20}
									/>
								</Grid>
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('aboutUs.content')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='contentZh'
									/>
								</Grid>
							</Grid>
						)}
						{currentTab == 'en' && (
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='titleEn'
										label={t('aboutUs.title')}
										maxLength={100}
									/>
								</Grid>
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('aboutUs.content')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='contentEn'
									/>
								</Grid>
							</Grid>
						)}
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
